import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["control", "controlId", "disabled", "label", "size"];
import PropTypes from 'prop-types';
import React, { forwardRef, useContext, useRef } from 'react';
import { LABELED_CONTROL_POSITIONS } from '../../constants';
import { identifier } from '../../util/identifier';
import { FormFieldContext } from '../FormField/FormField.context';
import { StyledLabeledControl } from './LabeledControl.styles';
var defaultProps = {
  labelPosition: LABELED_CONTROL_POSITIONS.right,
  size: 'md'
};

/**
 * @related ['Checkbox', 'Label', 'Radio', 'Switch']
 * @since 6.0.0
 */
export var LabeledControl = /*#__PURE__*/forwardRef(function (props, forwardedRef) {
  var _defaultProps$props = Object.assign({}, defaultProps, props),
    control = _defaultProps$props.control,
    controlId = _defaultProps$props.controlId,
    disabled = _defaultProps$props.disabled,
    label = _defaultProps$props.label,
    size = _defaultProps$props.size,
    rest = _objectWithoutProperties(_defaultProps$props, _excluded);
  var context = Object.assign({}, useContext(FormFieldContext), {
    // Create a default id to be used with the control and labels
    controlId: useRef(identifier()).current
  });
  if (typeof controlId !== 'undefined') {
    context.controlId = controlId;
  }
  if (typeof disabled !== 'undefined') {
    context.disabled = disabled;
  }
  if (typeof props.size !== 'undefined') {
    context.size = props.size;
  }
  var merged = Object.assign({}, defaultProps, context, props);

  // Note: selection control components should come first in the DOM tree so that
  // labels can trigger off :hover/:focus states.
  return /*#__PURE__*/React.createElement(StyledLabeledControl, _extends({
    ref: forwardedRef,
    size: merged.size
  }, rest), /*#__PURE__*/React.createElement(FormFieldContext.Provider, {
    value: context
  }, control, label));
});
LabeledControl.displayName = 'LabeledControl';
LabeledControl.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * A `Label` component.
   */
  label: PropTypes.node.isRequired,
  /**
   * A `Checkbox` or `Radio` component.
   */
  control: PropTypes.node.isRequired,
  /**
   * Identifier used for form controls and labels.
   * This will set `id` for form controls and `htmlFor` for
   * `Label` components.
   */
  controlId: PropTypes.string,
  /**
   * Displays the control in a disabled state. Can also be inherited from a FieldSet parent.
   *
   * @since 6.7.0
   */
  disabled: PropTypes.bool,
  /**
   * If `true`, this will span the entire width. Be careful: while appealing at mobile sizes,
   * this may result in too much space between the label and input at larger widths.
   */
  fluid: PropTypes.bool,
  /**
   * Where to align the label with respect to the selection control.
   */
  labelPosition: PropTypes.oneOf(Object.values(LABELED_CONTROL_POSITIONS)),
  /**
   * Sets the `size` of the control. This currently only works with Switches.
   *
   * @since 6.7.0
   */
  size: PropTypes.oneOf(['md', 'lg'])
} : {};